import React, {useState, useEffect} from "react";
import {useSelector, useDispatch} from "react-redux";
import {Box, Button, InputAdornment} from "@mui/material";
import {styled, useTheme} from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
// import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import {setUser} from "../store/userSlice";
import {setOrganization} from "../store/organziationSlice";
import {setPermissions} from "../store/permissionsSlice";
import authService from "../utils/authService";
import personService from "../api/person";
import CustomTextField from "../common/CustomTextField";
import {showMessage} from "../store/messageSlice";
import MessageSender from "../common/message/messageSender";
import LastButton from "../common/lastButton";

const gridContainer = {
    display: "grid",
    flexFlow: "row",
    gridTemplateRows: "repeat(4, 1fr)"
};
const StyledTab = styled((props) => <Tab disableRipple {...props} />)(({theme}) => ({
    "textTransform": "none",
    "fontWeight": theme.typography.fontWeightRegular,
    "fontSize": theme.typography.pxToRem(15),
    "width": "160px",
    "height": "36px",
    "minHeight": "36px",
    "backgroundColor": theme.palette.secondary.dark,
    "borderRadius": "11px",
    "color": theme.palette.custom.text,
    "&.Mui-selected": {
        color: theme.palette.custom.text,
        backgroundColor: theme.palette.secondary.main
    },
    "&.Mui-focusVisible": {
        backgroundColor: "rgba(100, 95, 228, 0.32)",
        color: theme.palette.primary
    },
    "&:hover": {
        color: theme.palette.primary.main
    }
}));
const StyledLoadingButton = styled((props) => <LoadingButton {...props} />)(({theme}) => ({
    "backgroundColor": theme.palette.primary.main,
    "&.MuiLoadingButton-loading": {
        backgroundColor: theme.palette.custom.light
    }
}));

function Login(props) {
    const {handleSubmit, signInMissing, customColor, signInLoading, handleForgetPassword} = props;
    const {t} = useTranslation();
    const theme = useTheme();

    return (
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{m: 2, pb: 1, textAlign: "center"}}>
            <CustomTextField
                margin="normal"
                fullWidth
                label={t("EmailOrNumber")}
                name="email"
                id="email"
                autoComplete="email"
                autoFocus
                variant="filled"
                sx={{
                    width: "100%"
                    // backgroundColor: theme.palette.custom.inputBackgroundColor,
                    // color: theme.palette.custom.inputTextColor,
                }}
                InputLabelProps={{
                    style: {color: theme.palette.custom.text}
                }}
                inputProps={{ maxLength: 254 }}
            />
            <CustomTextField
                margin="normal"
                fullWidth
                name="password"
                label={t("Password")}
                type="password"
                id="password"
                autoComplete="current-password"
                variant="filled"
                sx={{
                    width: "100%"
                    // backgroundColor: theme.palette.custom.inputBackgroundColor,
                    // color: theme.palette.custom.inputTextColor,
                }}
                InputLabelProps={{
                    style: {color: theme.palette.custom.text}
                }}
                inputProps={{ maxLength: 128 }}
            />
            {signInMissing && (
                <Typography
                    sx={{
                        mt: 1,
                        color: customColor ? customColor : theme.palette.custom.light
                    }}
                    variant="body2"
                    gutterBottom>
                    {t("signUpMissing")}
                </Typography>
            )}
            <StyledLoadingButton loading={signInLoading} loadingIndicator={<CircularProgress color="primary" size={16} />} type="submit" fullWidth variant="contained" sx={{mt: 3, mb: 1}}>
                {t("Login")}
            </StyledLoadingButton>
            <Link underline="none" sx={{color: theme.palette.custom.light}} href="#" onClick={handleForgetPassword} variant="body2">
                {t("Forgot Password")}
            </Link>
        </Box>
    );
}
Login.propTypes = {
    handleSubmit: PropTypes.func,
    signInMissing: PropTypes.bool,
    customColor: PropTypes.string,
    signInLoading: PropTypes.bool,
    handleForgetPassword: PropTypes.func
};
function Register(props) {
    const {handleSubmitRegister, requestOrVerify, customColor, showPassword, isError, errorText, signUpComment, signUpLoading, isForgetPassword, handleForgetPasswordBack, handleRequestByEmail, requestByEmail} = props;
    const {t} = useTranslation();
    const theme = useTheme();

    return (
        <Box component="form" onSubmit={handleSubmitRegister} noValidate sx={{m: 2, pb: 1, textAlign: "center"}}>
            {isForgetPassword && <LastButton onClick={handleForgetPasswordBack} />}
            <CustomTextField
                key={requestByEmail ? "email-field" : "mobile-field"}
                margin="normal"
                fullWidth
                label={requestByEmail ? t("Email") : t("Mobile Number")}
                name="email"
                id="email"
                autoComplete="email"
                autoFocus
                variant="filled"
                sx={{
                    width: "100%"
                }}
                InputLabelProps={{
                    style: {color: theme.palette.custom.text}
                }}
                InputProps={
                    !requestByEmail && {
                        startAdornment: <InputAdornment position="start">+</InputAdornment>
                    }
                }
            />
            {requestOrVerify && (
                <CustomTextField
                    margin="normal"
                    fullWidth
                    name="otp"
                    label={t("OTP")}
                    type="number"
                    id="otp"
                    autoFocus
                    variant="filled"
                    sx={{
                        width: "100%"
                    }}
                    InputLabelProps={{
                        style: {color: theme.palette.custom.text}
                    }}
                />
            )}
            {showPassword && (
                <>
                    <CustomTextField
                        margin="normal"
                        fullWidth
                        name="password"
                        label={t("Password")}
                        type="password"
                        id="password"
                        autoFocus
                        variant="filled"
                        error={isError}
                        helperText={errorText}
                        sx={{
                            width: "100%"
                        }}
                        InputLabelProps={{
                            style: {color: theme.palette.custom.text}
                        }}
                    />
                    <CustomTextField
                        margin="normal"
                        fullWidth
                        name="confirmPassword"
                        label={t("Confirm Password")}
                        type="password"
                        id="confirmPassword"
                        variant="filled"
                        error={isError}
                        helperText={errorText}
                        sx={{
                            width: "100%"
                        }}
                        InputLabelProps={{
                            style: {color: theme.palette.custom.text}
                        }}
                    />
                </>
            )}
            <Typography
                sx={{
                    mt: 1,
                    color: customColor ? customColor : theme.palette.custom.light
                }}
                variant="body2"
                gutterBottom>
                {t(signUpComment)}
            </Typography>
            {requestOrVerify && !requestByEmail && (
                <Button fullWidth variant="contained" sx={{mt: 1, mb: 1}} onClick={handleRequestByEmail}>
                    {t("Send Otp to Email")}
                </Button>
            )}
            <LoadingButton loading={signUpLoading} loadingIndicator={<CircularProgress color="primary" size={16} />} type="submit" fullWidth variant="contained" sx={{mt: 1, mb: 1}}>
                {!requestOrVerify && !showPassword ? t("RequestOtp") : requestOrVerify ? t("VerifyOtp") : isForgetPassword ? t("Change Password") : t("SignUp")}
            </LoadingButton>
            {/* {!isForgetPassword &&
        <Typography
          sx={{ mt: 1, mb: 1, color: theme.palette.custom.light }}
          variant="body2"
          gutterBottom
        >
          {t("BySigningUp")}
        </Typography>
      } */}
        </Box>
    );
}
Register.propTypes = {
    handleSubmitRegister: PropTypes.func,
    requestOrVerify: PropTypes.bool,
    customColor: PropTypes.string,
    showPassword: PropTypes.bool,
    isError: PropTypes.bool,
    errorText: PropTypes.string,
    signUpComment: PropTypes.string,
    signUpLoading: PropTypes.bool,
    isForgetPassword: PropTypes.bool,
    handleForgetPasswordBack: PropTypes.func,
    handleRequestByEmail: PropTypes.func,
    requestByEmail: PropTypes.bool
};

function LoginPage() {
    const theme = useTheme();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [tabValue, setTabValue] = useState(0);
    const [requestOrVerify, setRequestOrVerify] = useState(false);
    const [requestByEmail, setRequestByEmail] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [signUpComment, setSignUpComment] = useState(null);
    const [customColor, setCustomColor] = useState(null);
    const [signInLoading, setSignInLoading] = useState(false);
    const [signUpLoading, setSignUpLoading] = useState(false);
    const [signInMissing, setSignInMissing] = useState(false);
    const [isForgetPassword, setIsForgetPassword] = useState(false);
    const permissions = useSelector(({permissions}) => permissions);
    const user = useSelector(({user}) => user);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setIsForgetPassword(false);
        setRequestOrVerify(false);
        setRequestByEmail(false);
        setShowPassword(false);
        setSignUpComment(null);
    };

    useEffect(() => {
        // Only redirect if user and permissions are present (and permissions are non-empty)
        if (user && permissions && permissions.length > 0) {
            const from = location.state?.from?.pathname + location.state?.from?.search || "/";
            navigate(from, {replace: true});
        }
    }, [user, permissions, location.state, navigate]);
    const handleSubmit = (event) => {
        setSignInLoading(true);
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        if (data.get("password") !== "" && data.get("email") !== "") {
            setSignInMissing(false);
            authService
                .signInWithEmailAndPassword(data.get("email")?.trim(), data.get("password"))
                .then((res) => {
                    console.log(res)
                    const isActiveInAnyOrg = Array.isArray(res.organizations) && res.organizations.some((org) => org?.person_profile_info?.status?.toLowerCase() === "active");
                    if (!isActiveInAnyOrg) {
                        dispatch(
                            showMessage({
                                message: t("Your account is not active in any organization."),
                                autoHideDuration: 10000,
                                anchorOrigin: {
                                    vertical: "top",
                                    horizontal: "center"
                                },
                                variant: "error"
                            })
                        );
                        setSignInLoading(false);
                        return;
                    }
                    // console.log(res);
                    dispatch(setUser(res));
                    if (res?.organizations?.length) {
                        let activeOrganization = null;
                        try {
                            const cachedOrg = JSON.parse(localStorage.getItem("defaultOrganization"));
                            const validOrg = res.organizations.find((org) => org?.operation_profile_info?.uid && org.operation_profile_info.uid.toString() === cachedOrg?.uid.toString());
                            if (validOrg) {
                                activeOrganization = validOrg.operation_profile_info;
                            } else {
                                throw new Error("Cached org not valid for this user");
                            }
                        } catch (e) {
                            const nerospecOrg = res.organizations.find((org) => org?.operation_profile_info?.full_name?.toLowerCase()?.includes("nerospec"))?.operation_profile_info;

                            if (nerospecOrg) {
                                activeOrganization = nerospecOrg;
                            } else {
                                activeOrganization = res.organizations[0]?.operation_profile_info;
                            }
                            localStorage.setItem("defaultOrganization", JSON.stringify(activeOrganization));
                            const apiKey = activeOrganization?.api_key_list?.[0]?.key;
                            localStorage.setItem("apiKey", apiKey);
                        }

                        dispatch(setOrganization(activeOrganization));
                    }
                    personService
                        .getUserTypePermissions(res)
                        .then((json) => {
                            const UserPermissions = [];
                            if (json && json.app_user_type_permission_link && json.app_user_type_permission_link.length > 0) {
                                for (let i in json.app_user_type_permission_link)
                                    if (json.app_user_type_permission_link[i].user_type_uid.toString() === res.userType.toString()) {
                                        UserPermissions.push(json.app_user_type_permission_link[i].permission_secondary.permission_secondary_uid);
                                    }
                                localStorage.setItem("permissions", JSON.stringify(UserPermissions));
                                dispatch(setPermissions(UserPermissions));
                            }
                        })
                        .catch((errorr) => {
                            if (process.env.NODE_ENV === "development") console.log("getUserTypePermissionserrorr", errorr);
                        });
                    setSignInLoading(false);
                })
                .catch((_errors) => {
                    dispatch(
                        showMessage({
                            message: t("Invalid login information provided"),
                            autoHideDuration: 10000,
                            anchorOrigin: {
                                vertical: "top",
                                horizontal: "center"
                            },
                            variant: "error"
                        })
                    );
                    setSignInLoading(false);
                    if (process.env.NODE_ENV === "development") console.log("error register ", _errors);
                });
        } else {
            setSignInLoading(false);
            setSignInMissing(true);
        }
    };
    const handleForgetPassword = () => {
        setIsForgetPassword(true);
    };
    const handleRequestByEmail = () => {
        setRequestOrVerify(false);
        setRequestByEmail(true);
        setSignUpComment(null);
    };
    const handleSubmitRegister = (event) => {
        setSignUpLoading(true);
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        if (data.get("otp") === null && data.get("password") === null) {
            const body = JSON.stringify({
                auth: {
                    value: data.get("email")
                }
            });
            const headers = {
                "Content-Type": "application/json"
            };
            fetch(`${window.env.REACT_APP_API_IP}/ns-core/person/auth/otp/request`, {
                method: "post",
                headers,
                body
            })
                .then((response) => response.json())
                .then((json) => {
                    if (json && json.success === true && json.count > 0) {
                        setCustomColor(theme.palette.success.main);
                        setSignUpComment("OTPSent");
                        setRequestOrVerify(true);
                        // AuthCallbackFunction("OTP Sent");
                    } else {
                        setCustomColor(null);
                        setSignUpComment(json.msg);
                        // AuthCallbackFunction(json.msg);
                    }
                    setSignUpLoading(false);
                })
                .catch((err) => {
                    if (process.env.NODE_ENV === "development") console.log("err ", err);
                    setSignUpLoading(false);
                });
        } else if (data.get("otp") !== null && data.get("password") === null) {
            const body = JSON.stringify({
                auth: {
                    contact: data.get("email"),
                    otp: data.get("otp")
                }
            });
            const headers = {
                "Content-Type": "application/json"
            };
            fetch(`${window.env.REACT_APP_API_IP}/ns-core/person/auth/otp/verify`, {
                method: "post",
                headers,
                body
            })
                .then((response) => response.json())
                .then((json) => {
                    if (json && json.success === true) {
                        setCustomColor(theme.palette.success.main);
                        setSignUpComment("OTPVerified");
                        setRequestOrVerify(false);
                        setShowPassword(true);
                        //  AuthCallbackFunction("OTP Verified");
                    } else {
                        setCustomColor(null);
                        setSignUpComment(json.msg);
                        //  AuthCallbackFunction(json.msg);
                    }
                    setSignUpLoading(false);
                })
                .catch((err) => {
                    if (process.env.NODE_ENV === "development") console.log("err ", err);
                    setSignUpLoading(false);
                });
        } else {
            if (data.get("password").length < 8) {
                setIsError(true);
                setErrorText("Password too short");
                setSignUpLoading(false);
            } else if (data.get("password") !== data.get("confirmPassword")) {
                setIsError(true);
                setErrorText("Passwords do not match");
                setSignUpLoading(false);
            } else {
                setIsError(false);
                setErrorText(null);
                personService
                    .UserAuthValidate({email: data.get("email")})
                    .then((json) => {
                        if (json && json.success === true) {
                            const Person_Profile_ID = String(json.person_auth_validate_contact[0].person_profile_info.profile_uid);
                            personService
                                .SetUserpassword({
                                    id: Person_Profile_ID,
                                    password: data.get("password")
                                })
                                .then((passwordResult) => {
                                    if (passwordResult && json.success === true) {
                                        setCustomColor(theme.palette.success.main);
                                        setSignUpComment("PasswordChanged");
                                        setSignUpLoading(false);
                                        setIsForgetPassword(false);
                                        setTabValue(0);
                                        dispatch(
                                            showMessage({
                                                message: t("PasswordChanged"), //text or html
                                                autoHideDuration: 10000, //ms
                                                anchorOrigin: {
                                                    vertical: "top", //top bottom
                                                    horizontal: "center" //left center right
                                                },
                                                variant: "success" //success error info warning null
                                            })
                                        );
                                    } else {
                                        setCustomColor(theme.palette.error.main);
                                        setSignUpComment(passwordResult.msg);
                                        setSignUpLoading(false);
                                    }
                                })
                                .catch((_errors) => {
                                    if (process.env.NODE_ENV === "development") console.log("_errors ", _errors);
                                });
                        } else if (json) {
                            setCustomColor(null);
                            setSignUpComment(json.msg);
                            setSignUpLoading(false);
                        }
                    })
                    .catch((errorr) => {
                        if (process.env.NODE_ENV === "development") console.log("errorr ", errorr);
                        setSignUpLoading(false);
                    });
            }
        }
    };
    return (
        <Box
            sx={{
                [theme.breakpoints.down("sm")]: {
                    padding: "1rem 0 0 2.25rem "
                },
                [theme.breakpoints.down("md")]: {
                    padding: "1rem 0 0 4.25rem "
                },
                padding: "2rem 0 0 7rem ",
                ...gridContainer
            }}
            style={{
                backgroundImage: "url(background_simac.png)",
                backgroundSize: "cover",
                height: "100%",
                overflow: "hidden"
            }}>
            <div style={{height: "max-content"}}>
                <img width="200px;" src={window.env.REACT_APP_PUBLIC_URL + "/logo_nerospec_core.png"} alt="" />
            </div>
            <Box
                sx={{
                    [theme.breakpoints.up("md")]: {
                        display: "grid",
                        flexFlow: "column",
                        gridTemplateColumns: "repeat(2, 1fr)"
                    }
                }}>
                <Box
                    sx={{
                        [theme.breakpoints.down("md")]: {
                            width: "700px",
                            display: {xs: "none", sm: "block"},
                            margin: "5em auto"
                        },
                        width: "700px",
                        margin: "10em auto"
                    }}>
                    <img width="100%" src={window.env.REACT_APP_PUBLIC_URL + "/header_image_simac.png"} alt="logo" />
                </Box>
                <Box
                    sx={{
                        typography: "body1",
                        backgroundColor: theme.palette.secondary.main,
                        width: "320px",
                        margin: "4em auto",
                        boxShadow: "0px 1px 8px #2b2f38",
                        borderRadius: "11px"
                    }}>
                    <Tabs
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        TabIndicatorProps={{
                            style: {backgroundColor: theme.palette.custom.text}
                        }}
                        value={tabValue}
                        onChange={handleTabChange}
                        textColor="secondary">
                        <StyledTab label={t("Login")} />
                        <StyledTab label={t("Register")} />
                    </Tabs>
                    <div>
                        {tabValue === 0 && !isForgetPassword && <Login handleSubmit={handleSubmit} signInMissing={signInMissing} customColor={customColor} signInLoading={signInLoading} handleForgetPassword={handleForgetPassword} />}
                        {(tabValue === 1 || isForgetPassword) && (
                            <Register
                                handleSubmitRegister={handleSubmitRegister}
                                requestOrVerify={requestOrVerify}
                                customColor={customColor}
                                showPassword={showPassword}
                                isError={isError}
                                errorText={errorText}
                                signUpComment={signUpComment}
                                signUpLoading={signUpLoading}
                                isForgetPassword={isForgetPassword}
                                handleForgetPasswordBack={() => {
                                    setIsForgetPassword(false);
                                }}
                                handleRequestByEmail={handleRequestByEmail}
                                requestByEmail={requestByEmail}
                            />
                        )}
                    </div>
                </Box>
            </Box>
            <MessageSender />
        </Box>
    );
}

export default LoginPage;
